/**
 * Hook pour la reconnaissance vocale avec Web Speech API
 * Permet de transcrire la voix en texte pour le chat
 *
 * NOTE MOBILE: Safari iOS n'est pas supporté. Chrome Android devrait fonctionner
 * mais nécessite HTTPS et permissions micro explicites.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { logger } from "../lib/logger";
import { ErrorFactory, logError } from "../lib/error-handling";
import { VOICE_RECOGNITION_CONFIG } from "../config/voiceRecognition.config";

// Types pour Web Speech API (non inclus dans TypeScript par défaut)
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: ((this: SpeechRecognition, ev: Event) => void) | null;
  onend: ((this: SpeechRecognition, ev: Event) => void) | null;
  onerror:
    | ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => void)
    | null;
  onresult:
    | ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => void)
    | null;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionConstructor;
    webkitSpeechRecognition: SpeechRecognitionConstructor;
  }
}

/**
 * Détecte si on est sur Safari iOS (non supporté pour Web Speech API)
 */
function isIOSSafari(): boolean {
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  return isIOS && isSafari;
}

/**
 * Détecte si on est sur mobile
 */
function isMobileDevice(): boolean {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    ) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export interface VoiceRecognitionState {
  /** Texte transcrit en cours (interim) */
  interimTranscript: string;
  /** Texte transcrit final */
  finalTranscript: string;
  /** Est en train d'écouter */
  isListening: boolean;
  /** Erreur éventuelle */
  error: string | null;
  /** API supportée par le navigateur */
  isSupported: boolean;
  /** Est sur mobile (pour afficher des messages adaptés) */
  isMobile: boolean;
  /** Navigateur non supporté (ex: Safari iOS) */
  isUnsupportedBrowser: boolean;
}

export interface VoiceRecognitionActions {
  /** Démarrer l'écoute (async pour demander permissions sur mobile) */
  startListening: () => Promise<void>;
  /** Arrêter l'écoute */
  stopListening: () => void;
  /** Réinitialiser la transcription */
  resetTranscript: () => void;
}

export interface UseVoiceRecognitionOptions {
  /** Langue de reconnaissance (défaut: 'fr-FR') */
  lang?: string;
  /** Résultats intermédiaires (défaut: true) */
  interimResults?: boolean;
  /** Écoute continue (défaut: false) */
  continuous?: boolean;
  /** Callback quand transcription finale */
  onTranscriptChange?: (transcript: string) => void;
  /** Callback quand erreur */
  onError?: (error: string) => void;
}

/**
 * Hook pour utiliser la reconnaissance vocale
 */
export function useVoiceRecognition(
  options: UseVoiceRecognitionOptions = {},
): VoiceRecognitionState & VoiceRecognitionActions {
  const { t } = useTranslation("common");
  const {
    lang = "fr-FR",
    interimResults = true,
    continuous = false,
    onTranscriptChange,
    onError,
  } = options;

  const [isSupported, setIsSupported] = useState(() => {
    // Détecter le support immédiatement à l'initialisation
    if (isIOSSafari()) {
      return false;
    }

    const SpeechRecognitionAPI =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    return !!SpeechRecognitionAPI;
  });
  const [isListening, setIsListening] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isMobile] = useState(() => isMobileDevice());
  const [isUnsupportedBrowser] = useState(() => isIOSSafari());
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef<string>(""); // Ref pour persister entre les sessions
  const onTranscriptChangeRef = useRef(onTranscriptChange);
  const onErrorRef = useRef(onError);

  // Mettre à jour les refs quand les callbacks changent
  useEffect(() => {
    onTranscriptChangeRef.current = onTranscriptChange;
    onErrorRef.current = onError;
  }, [onTranscriptChange, onError]);

  useEffect(() => {
    // Safari iOS n'est pas supporté
    if (isUnsupportedBrowser) {
      setIsSupported(false);
      logger.warn(
        "Safari iOS ne supporte pas la reconnaissance vocale",
        "general",
      );
      return;
    }

    const SpeechRecognitionAPI =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognitionAPI) {
      setIsSupported(true);
      recognitionRef.current = new SpeechRecognitionAPI();
    } else {
      setIsSupported(false);
      logger.warn("Web Speech API non supportée par ce navigateur", "general");
    }
  }, [isUnsupportedBrowser]);

  // Configurer la reconnaissance vocale
  useEffect(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;

    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.lang = lang;

    recognition.onstart = () => {
      console.log("🎤 SPEECH RECOGNITION STARTED (CORE)");
      setIsListening(true);
      setError(null);
    };

    recognition.onend = () => {
      console.log("🎤 SPEECH RECOGNITION ENDED (CORE)", {
        autoRestart: VOICE_RECOGNITION_CONFIG.autoRestart,
        continuous,
        isListening,
      });
      // Configuration stable : PAS de redémarrage automatique
      // Voir voiceRecognition.config.ts pour l'explication
      if (VOICE_RECOGNITION_CONFIG.autoRestart && continuous && isListening) {
        logger.debug(
          "Redémarrage automatique reconnaissance vocale",
          "general",
        );
        setTimeout(() => {
          try {
            recognition.start();
          } catch (error) {
            logError(
              ErrorFactory.api(
                `Impossible de redémarrer la reconnaissance vocale: ${error}`,
                "Erreur lors du redémarrage automatique",
              ),
              { component: "voice-recognition", operation: "auto-restart" },
            );
            setIsListening(false);
          }
        }, VOICE_RECOGNITION_CONFIG.restartDelay);
      } else {
        console.log("🎤 SETTING ISLISTENING TO FALSE (CORE)");
        setIsListening(false);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      logError(
        ErrorFactory.api(
          `Erreur reconnaissance vocale: ${event.error}`,
          "Erreur de reconnaissance",
        ),
        { metadata: { errorType: event.error } },
      );

      // Ignorer l'erreur "no-speech" (silence normal, pas une vraie erreur)
      if (event.error === "no-speech") {
        logger.debug("Silence détecté, arrêt normal", "general");
        return;
      }

      let errorMessage = t("voice.error.generic");

      switch (event.error) {
        case "no-speech":
          errorMessage = t("voice.error.noSpeech");
          break;
        case "audio-capture":
          errorMessage = t("voice.error.audioCapture");
          break;
        case "not-allowed":
          errorMessage = t("voice.error.notAllowed");
          break;
        case "network":
          errorMessage = t("voice.error.network");
          break;
        case "aborted":
          errorMessage = t("voice.error.aborted");
          break;
        default:
          errorMessage = t("voice.error.withCode", { code: event.error });
      }

      setError(errorMessage);
      setIsListening(false);

      if (onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      // 🔧 FIX: Exactement comme remarkablemark
      // Boucler depuis resultIndex (pas depuis 0)
      let finalText = "";
      let interimText = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalText += transcript;
        } else {
          interimText += transcript;
        }
      }

      // Accumuler les résultats finaux avec la ref
      if (finalText) {
        finalTranscriptRef.current += finalText + " ";
        setFinalTranscript(finalTranscriptRef.current);
        if (onTranscriptChangeRef.current) {
          onTranscriptChangeRef.current(finalText);
        }
      }

      setInterimTranscript(interimText);
    };

    return () => {
      if (recognition && isListening) {
        recognition.stop();
      }
    };
  }, [lang, continuous, interimResults, isListening, t]);
  // onTranscriptChange et onError sont dans des refs, pas besoin de les mettre en dépendances

  const startListening = useCallback(async () => {
    const recognition = recognitionRef.current;
    console.log("🎤 STARTLISTENING CALLED (CORE):", {
      hasRecognition: !!recognition,
      isListening,
      isSupported,
      isMobile,
      isUnsupportedBrowser,
    });

    if (!recognition || isListening) return;

    // Sur mobile, demander explicitement la permission micro avant de démarrer
    if (isMobile) {
      try {
        // Vérifier et demander la permission micro
        console.log("🎤 REQUESTING MICROPHONE PERMISSION ON MOBILE (CORE)");
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        // Arrêter immédiatement le stream, on veut juste la permission
        stream.getTracks().forEach((track) => track.stop());
        logger.debug("Permission micro accordée sur mobile", "general");
        console.log("🎤 MICROPHONE PERMISSION GRANTED (CORE)");
      } catch (permissionError) {
        const errorMessage = t("voice.error.permissionDeniedMobile");
        setError(errorMessage);
        if (onErrorRef.current) {
          onErrorRef.current(errorMessage);
        }
        logError(
          ErrorFactory.api(
            "Permission micro refusée sur mobile",
            "Impossible d'accéder au micro",
          ),
          { metadata: { error: permissionError, isMobile: true } },
        );
        console.log("🎤 MICROPHONE PERMISSION DENIED (CORE):", permissionError);
        return;
      }
    }

    // NE PAS réinitialiser - on veut accumuler entre les sessions
    // setFinalTranscript("");
    // setInterimTranscript("");

    try {
      console.log("🎤 STARTING SPEECH RECOGNITION (CORE)");
      recognition.start();
    } catch (err: unknown) {
      // Ignorer l'erreur si déjà démarré
      if (err instanceof Error && err.message?.includes("already started")) {
        logger.debug("Reconnaissance déjà active", "general");
        console.log("🎤 RECOGNITION ALREADY STARTED (CORE)");
        return;
      }

      logError(
        ErrorFactory.api(
          "Erreur démarrage reconnaissance vocale",
          "Impossible de démarrer",
        ),
        { metadata: { error: err, isMobile } },
      );
      setError(t("voice.error.startFailed"));
      console.log("🎤 ERROR STARTING RECOGNITION (CORE):", err);
    }
  }, [isListening, isMobile, isSupported, isUnsupportedBrowser, t]);

  const stopListening = useCallback(() => {
    const recognition = recognitionRef.current;
    console.log("🎤 STOPLISTENING CALLED (CORE):", {
      hasRecognition: !!recognition,
      isListening,
    });

    if (!recognition) return;

    // 🔧 FIX: Mettre isListening à false AVANT d'arrêter pour éviter le redémarrage auto
    console.log("🎤 SETTING ISLISTENING TO FALSE BEFORE STOP (CORE)");
    setIsListening(false);

    try {
      console.log("🎤 STOPPING SPEECH RECOGNITION (CORE)");
      recognition.stop();
    } catch (err) {
      logError(
        ErrorFactory.api(
          "Erreur arrêt reconnaissance vocale",
          "Impossible d'arrêter",
        ),
        {
          metadata: { error: err },
        },
      );
      console.log("🎤 ERROR STOPPING RECOGNITION (CORE):", err);
    }
  }, [isListening]);

  const resetTranscript = useCallback(() => {
    finalTranscriptRef.current = "";
    setInterimTranscript("");
    setFinalTranscript("");
    setError(null);
  }, []);

  return {
    interimTranscript,
    finalTranscript,
    isListening,
    error,
    isSupported,
    isMobile,
    isUnsupportedBrowser,
    startListening,
    stopListening,
    resetTranscript,
  };
}
